import {
  createContext,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type RefObject,
  type SetStateAction,
} from 'react'
import type { RevEntry } from '../api'
import {
  api,
  type ActionResult,
  type NextAction,
  type PendingAction,
  type PlanBoard,
  type PlanOp,
  type PlanSnapshot,
  type Readiness,
  type RunReport,
  type ScheduleSpec,
} from '../api'

// PHASE_LABEL maps the decomposer's deepen phases to friendly progress text —
// the restructure phases first, then the narrative sub-phases when narrating.
export const PHASE_LABEL: Record<string, string> = {
  starting: 'starting',
  requirements: 'reading requirements',
  design: 'designing structure',
  tasks: 'breaking into tasks',
  assemble: 'assembling',
  publish: 'finalizing',
  // narrative (design-doc author) sub-phases
  narrative: 'writing the narrative',
  search: 'researching the codebase',
  diagrams: 'drawing diagrams',
}

const PHASE_INDEX: Record<string, number> = { requirements: 0, design: 1, tasks: 2 }
export function phaseIndex(phase: string): number {
  return PHASE_INDEX[phase] ?? 0
}

const DOC_PHASE_INDEX: Record<string, number> = { search: 0, diagrams: 1, assemble: 2, publish: 3 }
export function docPhaseIndex(phase: string): number {
  return DOC_PHASE_INDEX[phase] ?? 0
}

export interface ChatLine {
  id: number
  role: 'you' | 'claude'
  text: string
  applied?: ActionResult[]  // "changed:" chips — what this reply mutated
  pending?: PendingAction[] // expensive actions awaiting a Confirm/Dismiss
}

// LiveTask is one task's live state during a streamed run (running → terminal).
export type LiveTask = { task_id: string; title: string; skill?: string; status: string; step_state?: string }

// PanelId names the plan's inline disclosures — the sections that collapse under
// the assistant and open from a priority CTA or a header click.
export type PanelId = 'questions' | 'bid' | 'accuracy' | 'board' | 'run' | 'design-doc' | 'models' | 'settings'

export interface PlanContextValue {
  error: string | undefined
  snap: PlanSnapshot | null
  setSnap: Dispatch<SetStateAction<PlanSnapshot | null>>
  readiness: Readiness | null
  board: PlanBoard | null
  selected: string | null
  setSelected: Dispatch<SetStateAction<string | null>>
  chat: ChatLine[]
  input: string
  setInput: Dispatch<SetStateAction<string>>
  busy: boolean
  view: 'gantt' | 'graph' | 'kitchen'
  setView: Dispatch<SetStateAction<'gantt' | 'graph' | 'kitchen'>>
  runMsg: string | undefined
  setRunMsg: Dispatch<SetStateAction<string | undefined>>
  runReport: RunReport | null
  running: boolean
  liveTasks: Record<string, LiveTask>
  decomposing: boolean
  composing: boolean
  setComposing: Dispatch<SetStateAction<boolean>>
  decElapsed: number
  decActive: number
  decErr: string | null
  setDecErr: Dispatch<SetStateAction<string | null>>
  runErr: string | null
  setRunErr: Dispatch<SetStateAction<string | null>>
  showPerf: boolean
  setShowPerf: Dispatch<SetStateAction<boolean>>
  generatingDoc: boolean
  docErr: string | null
  docElapsed: number
  docActive: number
  ticket: string
  setTicket: Dispatch<SetStateAction<string>>
  ticketDesc: string
  setTicketDesc: Dispatch<SetStateAction<string>>
  history: RevEntry[] | null
  setHistory: Dispatch<SetStateAction<RevEntry[] | null>>
  rethinking: boolean
  rethinkPhase: string | undefined
  rethinkDone: boolean
  researching: boolean
  retiering: boolean
  pendingTier: number | null
  chatOpen: boolean
  setChatOpen: Dispatch<SetStateAction<boolean>>
  expanded: Set<PanelId>
  togglePanel: (id: PanelId) => void
  openPanel: (id: PanelId) => void
  chatEndRef: RefObject<HTMLDivElement>
  titleRef: RefObject<HTMLInputElement>
  switchPlan: (id: string) => void
  renamePlan: (id: string, title: string) => void
  setPlanClient: (id: string, client: string) => void
  deletePlan: (id: string) => void
  newPlan: () => void
  runPriority: (a: NextAction) => void
  decompose: (titleArg?: string, descArg?: string) => void
  openHistory: () => void
  deepen: () => void
  researchThenReview: () => void
  revertTo: (ev: RevEntry) => void
  rethink: () => void
  answerQuestion: (questionId: string, answer: string) => void
  setTier: (t: number) => void
  setTarget: (repo: string) => void
  setPlanStatus: (status: string) => void
  runPlan: (mode?: 'step') => void
  stopRun: () => void
  stepRespond: (taskId: string, choice: 'approve' | 'reject') => void
  schedulePlan: (spec: ScheduleSpec) => void
  unschedulePlan: () => void
  applyOp: (op: PlanOp) => void
  generateDoc: () => void
  stopDeepen: () => void
  sendChat: () => void
  confirmPending: (lineId: number, action: PendingAction) => void
  dismissPending: (lineId: number, action: PendingAction) => void
}

export const PlanContext = createContext<PlanContextValue | null>(null)

export function PlanProvider({ children }: { children: ReactNode }) {
  const [snap, setSnap] = useState<PlanSnapshot | null>(null)
  const [readiness, setReadiness] = useState<Readiness | null>(null)
  const [error, setError] = useState<string>()
  const [selected, setSelected] = useState<string | null>(null)
  const chatIdRef = useRef(0)
  const nextChatId = () => ++chatIdRef.current
  // One scannable line, not a wall of parenthetical lists — the console renders
  // example chips beside it for the "what can I say?" cases.
  const [chat, setChat] = useState<ChatLine[]>([
    {
      id: 0,
      role: 'claude',
      text: 'Tell me what to change and I do it — showing exactly what changed, and asking first before anything expensive.',
    },
  ])
  // The chat draft persists too: in the console the composer IS the interface,
  // so a reload must never eat a half-typed instruction.
  const [input, setInput] = useState(() => {
    try { return localStorage.getItem('xnapz.draft.chat') ?? '' } catch { return '' }
  })
  useEffect(() => {
    try { localStorage.setItem('xnapz.draft.chat', input) } catch { /* storage unavailable */ }
  }, [input])
  const [busy, setBusy] = useState(false)
  const [view, setView] = useState<'gantt' | 'graph' | 'kitchen'>('kitchen')
  const [runMsg, setRunMsg] = useState<string>()
  const [runReport, setRunReport] = useState<RunReport | null>(null)
  const [running, setRunning] = useState(false)
  const [liveTasks, setLiveTasks] = useState<Record<string, LiveTask>>({})
  const [decomposing, setDecomposing] = useState(false)
  // composing: the "＋ New plan" state — the active board hides so the compose
  // form is unmistakably about a NEW plan, not the one below it. Exits on
  // decompose success, plan switch, or explicit cancel.
  const [composing, setComposing] = useState(false)
  const [decElapsed, setDecElapsed] = useState(0)
  const [decActive, setDecActive] = useState(0)
  const [decErr, setDecErr] = useState<string | null>(null)
  const [runErr, setRunErr] = useState<string | null>(null)
  const [showPerf, setShowPerf] = useState(false)
  const [generatingDoc, setGeneratingDoc] = useState(false)
  const [docErr, setDocErr] = useState<string | null>(null)
  const [docElapsed, setDocElapsed] = useState(0)
  const [docActive, setDocActive] = useState(0)

  useEffect(() => {
    if (!decomposing) return
    setDecElapsed(0)
    const t0 = performance.now()
    const id = setInterval(() => setDecElapsed((performance.now() - t0) / 1000), 250)
    return () => clearInterval(id)
  }, [decomposing])
  useEffect(() => {
    if (!generatingDoc) return
    setDocElapsed(0)
    const t0 = performance.now()
    const id = setInterval(() => setDocElapsed((performance.now() - t0) / 1000), 250)
    return () => clearInterval(id)
  }, [generatingDoc])
  // The unsubmitted ticket draft persists across reload/crash so in-progress
  // intake is never lost (barometer Pillar 2.4 — state persistence).
  const [ticket, setTicket] = useState(() => {
    try { return localStorage.getItem('xnapz.draft.ticket') ?? '' } catch { return '' }
  })
  const [ticketDesc, setTicketDesc] = useState(() => {
    try { return localStorage.getItem('xnapz.draft.ticketDesc') ?? '' } catch { return '' }
  })
  useEffect(() => {
    try {
      localStorage.setItem('xnapz.draft.ticket', ticket)
      localStorage.setItem('xnapz.draft.ticketDesc', ticketDesc)
    } catch { /* storage unavailable — the draft simply won't persist */ }
  }, [ticket, ticketDesc])
  const chatEndRef = useRef<HTMLDivElement>(null)
  const titleRef = useRef<HTMLInputElement>(null)
  const pollRef = useRef<string | null>(null)
  const rethinkPollRef = useRef<number | null>(null)

  // Resume following an in-flight run after a page load (refresh-safe).
  useEffect(() => {
    const ar = snap?.active_run_id
    if (ar && pollRef.current == null && !running) {
      setBusy(true)
      followRun(ar)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snap?.active_run_id])

  const switchPlan = (id: string) => {
    if (busy) return
    setBusy(true)
    api.planSwitch(id).then((r) => { setSnap(r.snapshot); setSelected(null); setRunReport(null); setRunMsg(undefined); setComposing(false) })
      .catch((e) => setError(String(e))).finally(() => setBusy(false))
  }
  const renamePlan = (id: string, title: string) => {
    api.planRename(id, title).then((r) => setSnap(r.snapshot)).catch((e) => setError(String(e)))
  }
  const setPlanClient = (id: string, client: string) => {
    api.planSetClient(id, client).then((r) => setSnap(r.snapshot)).catch((e) => setError(String(e)))
  }
  const deletePlan = (id: string) => {
    setBusy(true)
    api.planDelete(id).then((r) => { setSnap(r.snapshot); setSelected(null) })
      .catch((e) => setError(String(e))).finally(() => setBusy(false))
  }
  const [history, setHistory] = useState<RevEntry[] | null>(null)
  const [rethinking, setRethinking] = useState(false)
  const [rethinkPhase, setRethinkPhase] = useState<string | undefined>()
  const [rethinkDone, setRethinkDone] = useState(false)
  // The research nudge shown before a Deepen when unknowns are still open.
  const [researching, setResearching] = useState(false)
  // expanded: which inline panels are open. The plan's home leads with the
  // assistant + priorities; questions/estimate/board/run/design-doc collapse into
  // disclosures a priority (or a click) opens. Collapsed by default; the run panel
  // auto-opens while a run is live so progress is never hidden.
  const [expanded, setExpanded] = useState<Set<PanelId>>(() => new Set())
  const togglePanel = (id: PanelId) =>
    setExpanded((s) => {
      const next = new Set(s)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  const openPanel = (id: PanelId) => setExpanded((s) => (s.has(id) ? s : new Set(s).add(id)))
  useEffect(() => {
    if (running) openPanel('run')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running])
  const [retiering, setRetiering] = useState(false)
  const [pendingTier, setPendingTier] = useState<number | null>(null)
  const [chatOpen, setChatOpen] = useState(true)
  const openHistory = () => {
    if (!board?.id) return
    api.planHistory(board.id).then((r) => setHistory(r.history)).catch((e) => setRunMsg(String(e)))
  }
  const revertTo = (ev: RevEntry) => {
    if (!board?.id) return
    if (!confirm(`Restore the plan to revision ${ev.revision} (${ev.summary})? The current state stays in history.`)) return
    setBusy(true)
    api.planRevert(board.id, ev.event_id)
      .then((r) => { setSnap(r.snapshot); setHistory(null); setRunMsg(`Restored to revision ${ev.revision} — the restore itself is a new history entry`) })
      .catch((e) => setRunMsg(String(e)))
      .finally(() => setBusy(false))
  }
  const rethink = () => {
    if (!board?.id || rethinking) return
    if (!confirm('Think deep: re-architect the plan top-down from the refined requirements (answered questions, type, current structure), then refresh the full estimate against the new structure. Runs the stronger model for a few minutes in the background — the current plan stays one History click away.')) return
    rethinkNow()
  }
  // rethinkNow kicks the think-deep job without the confirm dialog — used when the
  // action was already confirmed elsewhere (e.g. a chat pending-action card).
  const rethinkNow = (narrate = false) => {
    if (!board?.id || rethinking) return
    const planID = board.id
    const verb = narrate ? 'Deepening' : 'Thinking deep'
    const tail = narrate ? 'restructuring and writing the narrative' : 're-architecting'
    setRethinking(true)
    setRethinkDone(false)
    setRethinkPhase('starting')
    setRunMsg(`🧠 ${verb} — ${tail} on the stronger model (runs in the background, a few minutes)…`)
    const started = Date.now()
    api.planRethink(planID, narrate).catch((e) => { setRunMsg(String(e)); setRethinking(false); setRethinkPhase(undefined) })
    const poll = window.setInterval(() => {
      api.rethinkStatus(planID)
        .then((r) => {
          const j = r.job
          if (j.running) {
            const secs = Math.round((Date.now() - started) / 1000)
            const ph = PHASE_LABEL[j.phase ?? ''] ?? j.phase ?? 'working'
            setRethinkPhase(ph)
            setRunMsg(`🧠 ${verb} — ${ph}… (${secs}s · stronger model; safe to keep working, the result lands even if you navigate away)`)
            return
          }
          window.clearInterval(poll)
          rethinkPollRef.current = null
          setRethinking(false)
          setRethinkPhase(undefined)
          if (j.stopped) setRunMsg(`■ ${narrate ? 'Deepen' : 'Think deep'} stopped — the plan is unchanged.`)
          else if (j.error) setRunMsg(`⚠ ${narrate ? 'Deepen' : 'Think deep'} failed: ${j.error}`)
          else if (j.done) {
            api.planning().then(setSnap)
            setRethinkDone(true)
            setRunMsg(`✓ ${narrate ? 'Deepen' : 'Think deep'} complete — review the new structure${narrate ? ', narrative,' : ''} and follow-up questions; the old plan is one History click away`)
            window.setTimeout(() => setRethinkDone(false), 8000)
          }
        })
        .catch(() => {})
    }, 2000)
    rethinkPollRef.current = poll
  }

  // stopDeepen cancels a running Deepen/rethink. The frontend clears its own
  // state immediately (optimistic) and tells the server to cancel the job; if it
  // hadn't restructured yet, the plan is left untouched.
  const stopDeepen = () => {
    if (!board?.id) return
    if (rethinkPollRef.current) {
      window.clearInterval(rethinkPollRef.current)
      rethinkPollRef.current = null
    }
    setRethinking(false)
    setRethinkPhase(undefined)
    setRunMsg('■ Stopping the Deepen…')
    api
      .rethinkStop(board.id)
      .then(() => {
        setRunMsg('■ Deepen stopped — the plan is unchanged.')
        api.planning().then(setSnap) // reconcile in case the narrative phase had landed
      })
      .catch((e) => setRunMsg('Stop failed: ' + String(e)))
  }

  // deepen is the unified pass: restructure → narrate on the strong model. The
  // research-first ordering that the old nudge enforced is now the ranker's job —
  // it recommends "research" above "deepen" while questions are open — so Deepen
  // just runs (the priorities guide the order; they don't block it).
  const deepen = () => {
    if (!board?.id || rethinking) return
    rethinkNow(true)
  }

  // researchThenReview runs the cheap recommendation-backed research; answers land
  // as editable and the priority list re-ranks off the refreshed readiness.
  const researchThenReview = () => {
    if (!board?.id || researching) return
    setResearching(true)
    api
      .planResearch(board.id)
      .then((r) => {
        setSnap(r.snapshot)
      })
      .catch((e) => setRunMsg(String(e)))
      .finally(() => setResearching(false))
  }

  const newPlan = () => {
    setComposing(true)
    setTicket('')
    setTicketDesc('')
    setDecErr(null)
    setSelected(null)
    setTimeout(() => titleRef.current?.focus(), 0)
  }

  useEffect(() => {
    api.planning().then(setSnap).catch((e) => setError(String(e)))
  }, [])

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [chat])

  // Readiness is the ranked "top priorities" for the active plan — refetched on
  // every plan change AND polled every 5s so the auto-advance countdown stays
  // live (the server pushes fire_at back whenever the user keeps editing).
  useEffect(() => {
    const id = snap?.board?.id
    if (!id) return
    let live = true
    const fetchIt = () => api.readiness(id).then((r) => { if (live) setReadiness(r) }).catch(() => {})
    fetchIt()
    const t = window.setInterval(fetchIt, 5000)
    return () => {
      live = false
      window.clearInterval(t)
    }
  }, [snap?.board?.id, snap?.board?.revision])

  // When an auto-advance stage fires (the pending stage changes or clears),
  // refresh the snapshot so researched answers / restructures / the refreshed
  // estimate appear without a manual reload.
  const autoStageRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    const stage = readiness?.auto_next?.stage
    if (autoStageRef.current !== undefined && autoStageRef.current !== stage) {
      api.planning().then(setSnap).catch(() => {})
    }
    autoStageRef.current = stage
  }, [readiness?.auto_next?.stage])

  const board = snap?.board ?? null

  const applyOp = (op: PlanOp) => {
    setBusy(true)
    api
      .planningOp(op)
      .then((r) => setSnap(r.snapshot))
      .catch((e) => setError(String(e)))
      .finally(() => setBusy(false))
  }

  // The tier lever re-runs assignment server-side (per-task skill matching), which
  // can take a moment with a real embedder. Move the slider optimistically and
  // show a "reassigning…" state until the new board lands, so it never feels stuck.
  const setTier = (t: number) => {
    setPendingTier(t)
    setRetiering(true)
    api
      .planningOp({ kind: 'tier', tier: t })
      .then((r) => setSnap(r.snapshot))
      .catch((e) => setError(String(e)))
      .finally(() => {
        setRetiering(false)
        setPendingTier(null)
      })
  }

  const sendChat = () => {
    const msg = input.trim()
    if (!msg || busy) return
    setChat((c) => [...c, { id: nextChatId(), role: 'you', text: msg }])
    setInput('')
    setBusy(true)
    api
      .planningChat(msg)
      .then((r) => {
        setSnap(r.snapshot)
        setChat((c) => [...c, { id: nextChatId(), role: 'claude', text: r.reply, applied: r.applied, pending: r.pending }])
      })
      .catch((e) => setChat((c) => [...c, { id: nextChatId(), role: 'claude', text: 'Error: ' + String(e) }]))
      .finally(() => setBusy(false))
  }

  // clearPending drops one pending action from the chat line it belongs to (after the
  // user confirms or dismisses it) so its card doesn't linger.
  const clearPending = (lineId: number, action: PendingAction) => {
    setChat((c) => c.map((l) => (l.id === lineId ? { ...l, pending: (l.pending ?? []).filter((p) => p !== action) } : l)))
  }

  // confirmPending runs an expensive action the user approved from its chat card.
  // Sync actions (generate/propose bid) carry an op replayed via /chat/confirm; async
  // ones (rethink/decompose) kick the existing background job + its progress UI.
  const confirmPending = (lineId: number, action: PendingAction) => {
    clearPending(lineId, action)
    if (action.op) {
      setBusy(true)
      api
        .chatConfirm(action.op)
        .then((r) => {
          setSnap(r.snapshot)
          setChat((c) => [...c, { id: nextChatId(), role: 'claude', text: r.reply }])
        })
        .catch((e) => setChat((c) => [...c, { id: nextChatId(), role: 'claude', text: 'Error: ' + String(e) }]))
        .finally(() => setBusy(false))
      return
    }
    if (action.endpoint === 'rethink') {
      setChat((c) => [...c, { id: nextChatId(), role: 'claude', text: '🧠 Thinking deep — re-architecting in the background; watch the banner above the board.' }])
      rethinkNow()
    } else if (action.endpoint === 'decompose') {
      setChat((c) => [...c, { id: nextChatId(), role: 'claude', text: 'Planning that out as a new plan — building it now.' }])
      decompose(action.description ?? action.summary ?? '', '')
    }
  }

  const dismissPending = (lineId: number, action: PendingAction) => clearPending(lineId, action)

  // followRun polls a run until it reaches a terminal state, driving the live board +
  // run panel. It's refresh-safe: the run lives server-side, so reloading reconnects.
  const followRun = (runId: string) => {
    if (pollRef.current === runId) return
    pollRef.current = runId
    setRunning(true)
    setRunErr(null)
    setRunReport(null)
    setRunMsg(undefined)
    const TERMINAL = ['succeeded', 'failed', 'partial', 'interrupted']
    const tick = () => {
      if (pollRef.current !== runId) return // superseded (new run / unmount)
      api
        .runGet(runId)
        .then((run) => {
          if (pollRef.current !== runId) return
          const lt: Record<string, LiveTask> = {}
          run.tasks.forEach((t) => {
            lt[t.task_id] = { task_id: t.task_id, title: t.title, skill: t.skill, status: t.status, step_state: t.step_state }
          })
          setLiveTasks(lt)
          if (TERMINAL.includes(run.status)) {
            pollRef.current = null
            setRunReport({
              run_id: run.id,
              recorded: 0,
              results: run.tasks.map((t) => ({
                task_id: t.task_id,
                title: t.title,
                skill: t.skill,
                status: t.status,
                note: t.note,
                output: t.output,
              })),
            })
            setRunMsg(`${run.status} · ${run.passed} passed · ${run.failed} failed`)
            setRunning(false)
            setBusy(false)
            api.planning().then(setSnap).catch(() => {}) // board reflects updated statuses
            return
          }
          window.setTimeout(tick, 1500)
        })
        .catch((e) => {
          pollRef.current = null
          setRunErr(String(e))
          setRunning(false)
          setBusy(false)
        })
    }
    tick()
  }

  const runPlan = (mode?: 'step') => {
    if (busy || running) return
    setBusy(true)
    setRunErr(null)
    setRunReport(null)
    setLiveTasks({})
    setRunMsg(mode === 'step' ? 'Starting step-by-step run…' : 'Starting run…')
    api
      .runStart(mode)
      .then((r) => followRun(r.run_id))
      .catch((e) => {
        setRunMsg(undefined)
        setRunErr(String(e))
        setBusy(false)
      })
  }

  // stepRespond disposes an awaiting step-mode gate on the live run; the run
  // poll picks the state change up on its next tick.
  const stepRespond = (taskId: string, choice: 'approve' | 'reject') => {
    const id = pollRef.current ?? snap?.active_run_id
    if (!id) return
    api.runStep(id, taskId, choice).catch((e) => setRunErr(String(e)))
  }

  const stopRun = () => {
    const id = pollRef.current ?? snap?.active_run_id
    if (!id) return
    setRunMsg('Stopping run…')
    api.runStop(id).catch((e) => setRunErr(String(e)))
    // followRun's poll picks up the "interrupted" status and finalizes the panel.
  }

  const decompose = (titleArg?: string, descArg?: string) => {
    const title = (titleArg ?? ticket).trim()
    const desc = (descArg ?? ticketDesc).trim()
    if (!title || busy) return
    setComposing(true) // show the compose banner while the new plan builds
    setBusy(true)
    setDecomposing(true)
    setDecActive(0)
    setDecErr(null)
    setRunMsg(undefined)
    api
      .planningDecomposeStream(title, desc, (phase, status) => {
        if (status === 'start') setDecActive(phaseIndex(phase))
      })
      .then((r) => {
        setSnap(r.snapshot)
        setSelected(null)
        setTicket('')
        setTicketDesc('')
        setComposing(false)
      })
      .catch((e) => setDecErr(String(e)))
      .finally(() => {
        setBusy(false)
        setDecomposing(false)
      })
  }

  const setTarget = (repo: string) => {
    if (!board?.id) return
    api.planSetTarget(board.id, repo).then((r) => setSnap(r.snapshot)).catch((e) => setError(String(e)))
  }
  const setPlanStatus = (status: string) => {
    if (!board?.id) return
    api.planSetStatus(board.id, status).then((r) => setSnap(r.snapshot)).catch((e) => setError(String(e)))
  }
  const schedulePlan = (spec: ScheduleSpec) => {
    if (!board?.id) return
    api.planSchedule(board.id, spec).then((r) => setSnap(r.snapshot)).catch((e) => setError(String(e)))
  }
  const unschedulePlan = () => {
    if (!board?.id) return
    api.planUnschedule(board.id).then((r) => setSnap(r.snapshot)).catch((e) => setError(String(e)))
  }
  const generateDoc = () => {
    if (generatingDoc) return
    setGeneratingDoc(true)
    setDocActive(0)
    setDocErr(null)
    api
      .generateDesignDocStream((phase, status) => {
        if (status === 'start') setDocActive(docPhaseIndex(phase))
      })
      .then((r) => setSnap(r.snapshot))
      .catch((e) => setDocErr(String(e)))
      .finally(() => setGeneratingDoc(false))
  }
  const answerQuestion = (questionId: string, answer: string) => {
    if (!board?.id) return
    api.answerQuestion(board.id, questionId, answer).then((r) => setSnap(r.snapshot)).catch((e) => setError(String(e)))
  }

  // runPriority dispatches a priority card's [do it] to the plan's existing trigger
  // for that action id, and opens the inline panel where the result shows so the
  // user isn't left staring at a collapsed disclosure. generate/propose replay
  // through the universal-editor confirm path (same code chat's pending-cards use).
  const runPriority = (a: NextAction) => {
    switch (a.id) {
      case 'research':
        openPanel('questions')
        researchThenReview()
        break
      case 'deepen':
        deepen()
        break
      case 'finalize':
        setPlanStatus('finalized')
        break
      case 'run':
        openPanel('run')
        runPlan()
        break
      case 'generate_bid':
      case 'propose_bid':
        openPanel('bid')
        if (a.op && !busy) {
          setBusy(true)
          api
            .chatConfirm(a.op)
            .then((r) => setSnap(r.snapshot))
            .catch((e) => setError(String(e)))
            .finally(() => setBusy(false))
        }
        break
      case 'answer_criticals':
        openPanel('questions')
        break
      case 'schedule':
        openPanel('run')
        break
    }
  }

  const value: PlanContextValue = {
    error,
    snap,
    setSnap,
    readiness,
    board,
    selected,
    setSelected,
    chat,
    input,
    setInput,
    busy,
    view,
    setView,
    runMsg,
    setRunMsg,
    runReport,
    running,
    liveTasks,
    decomposing,
    composing,
    setComposing,
    decElapsed,
    decActive,
    decErr,
    setDecErr,
    runErr,
    setRunErr,
    showPerf,
    setShowPerf,
    generatingDoc,
    docErr,
    docElapsed,
    docActive,
    ticket,
    setTicket,
    ticketDesc,
    setTicketDesc,
    history,
    setHistory,
    rethinking,
    rethinkPhase,
    rethinkDone,
    researching,
    retiering,
    pendingTier,
    chatOpen,
    setChatOpen,
    expanded,
    togglePanel,
    openPanel,
    chatEndRef,
    titleRef,
    switchPlan,
    renamePlan,
    setPlanClient,
    deletePlan,
    newPlan,
    runPriority,
    decompose,
    openHistory,
    deepen,
    researchThenReview,
    revertTo,
    rethink,
    answerQuestion,
    setTier,
    setTarget,
    setPlanStatus,
    runPlan,
    stopRun,
    stepRespond,
    schedulePlan,
    unschedulePlan,
    applyOp,
    generateDoc,
    stopDeepen,
    sendChat,
    confirmPending,
    dismissPending,
  }

  return <PlanContext.Provider value={value}>{children}</PlanContext.Provider>
}
